export const glsl = (raw, ...values) => String.raw({raw}, ...values);

export function createProgram(gl, {
    vertex,
    fragment,
}) {
    const program = gl.createProgram();
    const shaders = {
        vertex: createShader(gl, gl.VERTEX_SHADER, vertex),
        fragment: createShader(gl, gl.FRAGMENT_SHADER, fragment),
    };
    gl.attachShader(program, shaders.vertex);
    gl.attachShader(program, shaders.fragment);
    gl.linkProgram(program);

    if (gl.getProgramParameter(program, gl.LINK_STATUS))
        return program;

    let err = gl.getProgramInfoLog(program);
    ['vertex', 'fragment'].map(type => {
        const log = gl.getShaderInfoLog(shaders[type]);
        if (log)
            err += `-- ${type}\n` + log;
    });
    throw err;
}

export function createShader(gl, type, source) {
    const prelude = [
        '#version 300 es',
        'precision highp float;',
    ];
    source = [ ...prelude, source ].join('\n');
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    // if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    //     const log = gl.getShaderInfoLog(shader);
    //     throw log;
    // }
    return shader;
}

export function createVAO(gl, program, binding) {
    const vao = gl.createVertexArray();
    gl.bindVertexArray(vao);
    for (let [name, options] of Object.entries(binding)) {
        if (!options) {
            console.log(`createVAO: ignoring empty attribute "${name}"`);
            continue;
        }

        if (name == 'elements') {
            // element array buffer
            options = {
                buffer: null,
                type: gl.UNSIGNED_SHORT,
                ...options,
            };
            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, options.buffer);
            //console.log('bind element array buffer:', options.buffer);
            continue;
        }


        // vertex array buffer
        const index = gl.getAttribLocation(program, name);
        if (index < 0) {
            console.warn(`createVAO: "${pgm.name}": unknown attribute "${name}"`);
            continue;
        }

        // vertex array buffer
        options = {
            buffer: null,
            size: 4,
            type: gl.FLOAT,
            stride: 0,
            offset: 0,
            normalized: false,
            divisor: 0,
            ...options,
        };

        // gl.getError()

        if (options.buffer) {
            gl.enableVertexAttribArray(index);
            if (options.divisor)
                gl.vertexAttribDivisor(index, options.divisor);
            gl.bindBuffer(gl.ARRAY_BUFFER, options.buffer);
            gl.vertexAttribPointer(
                index,
                options.size,
                options.type,
                options.normalized,
                options.stride,
                options.offset);
        } else {
            gl.disableVertexAttribArray(index);
            gl.bindBuffer(gl.ARRAY_BUFFER, null);
            let value = options.value ?
                new Float32Array(options.value) :
                new Float32Array(options.size);
            switch (options.size) {
                case 1:
                    gl.vertexAttrib1fv(index, value);
                    break;
                case 2:
                    gl.vertexAttrib2fv(index, value);
                    break;
                case 3:
                    gl.vertexAttrib3fv(index, value);
                    break;
                case 4:
                    gl.vertexAttrib4fv(index, value);
                    break;
            }
        }
    }
    gl.bindVertexArray(null);
    return vao;
}

export function createQuadVAO(gl, program, binding) {
    const bufQuad = createBuffer(gl, gl.ARRAY_BUFFER, [0,0, 1,0, 0,1, 1,1]);
    return createVAO(gl, program, {
        aCoord: {
            buffer: bufQuad,
            size: 2,
        },
        ...binding,
    });
}

export function createBuffer(gl, target, data=null, usage) {
    if (Array.isArray(data)) {
        if (target === gl.ELEMENT_ARRAY_BUFFER)
            data = new Uint16Array(data);
        else
            data = new Float32Array(data);
    }
    const buffer = gl.createBuffer();
    gl.bindBuffer(target, buffer);
    if (data)
        gl.bufferData(target, data, usage || gl.STATIC_DRAW);
    return buffer;
}

export class GLState {
    constructor(gl) {
        this.gl = gl;
        this.stack = [];
    }

    push(...pnames) {
        const {gl, stack} = this;
        const state = new Map;
        for (let pname of pnames) {
            const value = gl.getParameter(pname);
            state.set(pname, value);

            if (pname === gl.BLEND) {
                state.set(gl.BLEND_SRC_RGB, gl.getParameter(gl.BLEND_SRC_RGB));
                state.set(gl.BLEND_DST_RGB, gl.getParameter(gl.BLEND_DST_RGB));
                state.set(gl.BLEND_SRC_ALPHA, gl.getParameter(gl.BLEND_SRC_ALPHA));
                state.set(gl.BLEND_DST_ALPHA, gl.getParameter(gl.BLEND_DST_ALPHA));
            }
        }
        stack.push(state);
    }

    pop() {
        const {gl, stack} = this;
        const state = stack.pop();
        for (let [pname, value] of state.entries()) {
            this.set(pname, value);

            if (pname === gl.BLEND) {
                gl.blendFuncSeparate(
                    state.get(gl.BLEND_SRC_RGB),
                    state.get(gl.BLEND_DST_RGB),
                    state.get(gl.BLEND_SRC_ALPHA),
                    state.get(gl.BLEND_DST_ALPHA));
            }
        }
    }

    set(pname, value) {
        const {gl} = this;
        //console.log('set:', getGLEnumString(gl, pname), value);

        if (pname === gl.VIEWPORT)
            gl.viewport(...value);

        if (pname === gl.CURRENT_PROGRAM)
            gl.useProgram(value);

        if (pname === gl.ARRAY_BUFFER_BINDING)
            gl.bindBuffer(gl.ARRAY_BUFFER, value);

        if (pname === gl.ELEMENT_ARRAY_BUFFER_BINDING)
            gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, value);

        if (pname === gl.VERTEX_ARRAY_BINDING)
            gl.bindVertexArray(value);

        if (pname === gl.FRAMEBUFFER_BINDING)
            gl.bindFramebuffer(gl.FRAMEBUFFER, value);

        if (pname === gl.UNPACK_FLIP_Y_WEBGL)
            gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, value);

        if (pname === gl.TEXTURE_BINDING_2D)
            gl.bindTexture(gl.TEXTURE_2D, value);

        if (pname === gl.TEXTURE_BINDING_CUBE_MAP)
            gl.bindTexture(gl.TEXTURE_CUBE_MAP, value);

        if (pname === gl.BLEND ||
            pname === gl.DEPTH_TEST ||
            pname === gl.DEPTH_WRITEMASK ||
            pname === gl.SCISSOR_TEST)
        {
            value ? gl.enable(pname) : gl.disable(pname);
        }
    }

    scoped(pnames, callback) {
        this.push(...pnames);
        let value = callback();
        this.pop();
        return value;
    }
}

export function getGLEnumString(gl, value) {
    for (let name in gl)
        if (gl[name] === value)
            return name;
    return '<unknown>';
}

